import * as React from "react";
import { useRef } from "react";
import { Combobox, Option } from "@fluentui/react-components";
import { Add20Regular, ArrowCounterclockwise20Regular, Dismiss12Regular, Wand20Regular } from "@fluentui/react-icons";
import Button from "../Button";
import { mapColumnDataTypeToFriendlyLabel, type ColumnRow } from "../TemplateServices";
import { ActionIconButton, SectionHeader, type TemplateTabViewStyles } from "./TemplateTabViewStyles";
import type { ColumnRowDefinition } from "./TemplateTabViewTypes";

type TemplateColumnsSectionProps = {
  styles: TemplateTabViewStyles;
  isConfigurationLocked: boolean;
  loadingColumns: boolean;
  selectedColumns: ColumnRow[];
  availableColumns: ColumnRowDefinition[];
  showColumnPicker: boolean;
  setShowColumnPicker: (value: boolean) => void;
  setShowFilterPicker: (value: boolean) => void;
  handleAddColumn: (colKey: string) => void;
  handleResetColumns: () => void;
  handleRemoveColumn: (key: string) => void;
  handleColumnDisplayNameChange: (key: string, newDisplayName: string) => void;
  handleColumnExcelFormatChange: (key: string, excelFormat: string) => void;
  handleColumnDrop: (fromIndex: number, toIndex: number) => void;
};

const TemplateColumnsSection: React.FC<TemplateColumnsSectionProps> = ({
  styles,
  isConfigurationLocked,
  loadingColumns,
  selectedColumns,
  availableColumns,
  showColumnPicker,
  setShowColumnPicker,
  setShowFilterPicker,
  handleAddColumn,
  handleResetColumns,
  handleRemoveColumn,
  handleColumnDisplayNameChange,
  handleColumnExcelFormatChange,
  handleColumnDrop,
}) => {
  const dragColumnRef = useRef<number | null>(null);
  const unselectedColumns = availableColumns.filter((column) => !selectedColumns.find((row) => row.key === column.key));

  return (
    <div className={styles.section}>
      <SectionHeader styles={styles} num={3} title="Select columns (data)" />
      <div style={{ fontSize: "11px", color: "#6D6E71", padding: "6px 12px 8px 12px", borderBottom: "0.25pt solid #E8F0F0" }}>
        Choose the columns to display in the report and set the order.
      </div>
      <div className={styles.sectionBody}>
        <div className={styles.toolbarRow}>
          <Button
            icon={<Add20Regular style={{ fontSize: "14px" }} />}
            label="Add Column"
            onClick={() => { setShowColumnPicker(!showColumnPicker); setShowFilterPicker(false); }}
            disabled={isConfigurationLocked || unselectedColumns.length === 0 || loadingColumns}
          />
          <Button
            icon={selectedColumns.length > 0
              ? <ArrowCounterclockwise20Regular style={{ fontSize: "14px" }} />
              : <Wand20Regular style={{ fontSize: "14px" }} />}
            label={selectedColumns.length > 0 ? "Reset" : "Auto-suggest"}
            onClick={handleResetColumns}
            disabled={isConfigurationLocked || availableColumns.length === 0 || loadingColumns}
          />
        </div>

        {loadingColumns && (
          <div style={{ fontSize: "11px", color: "#666", padding: "8px 12px", fontStyle: "italic" }}>
            Loading columns...
          </div>
        )}

        {showColumnPicker && !isConfigurationLocked && unselectedColumns.length > 0 && (
          <Combobox
            autoComplete="off"
            placeholder="Pick a column to add..."
            onOptionSelect={(_, data) => { if (data.optionValue) handleAddColumn(data.optionValue); }}
            listbox={{ style: { maxHeight: "200px", overflowY: "auto" } }}
            style={{ width: "160px", fontSize: "11px", marginBottom: "8px" }}
            positioning={{
              position: "below",
              align: "start",
              pinned: true,
              autoSize: "width",
            }}
          >
            {unselectedColumns.map((column) => (
              <Option key={column.key} value={column.key} text={column.displayName}>
                {column.displayName}
              </Option>
            ))}
          </Combobox>
        )}

        {selectedColumns.length > 0 ? (
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead className={styles.tableHead}>
                <tr>
                  <th className={styles.th} style={{ width: "16px" }}></th>
                  <th className={styles.th} style={{ width: "24px" }}>Order</th>
                  <th className={styles.th}>Displayed As</th>
                  <th className={styles.th} style={{ width: "120px" }}>Excel Format</th>
                  <th className={styles.th} style={{ width: "30px" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {selectedColumns.map((column, index) => (
                  <tr
                    key={column.key}
                    onDragOver={(event) => event.preventDefault()}
                    onDrop={() => {
                      if (dragColumnRef.current !== null) {
                        handleColumnDrop(dragColumnRef.current, index);
                        dragColumnRef.current = null;
                      }
                    }}
                  >
                    <td className={styles.td}>
                      <span
                        className={styles.dragHandle}
                        title="Drag to reorder"
                        draggable={!isConfigurationLocked}
                        onDragStart={(event) => {
                          dragColumnRef.current = index;
                          event.dataTransfer.effectAllowed = "move";
                        }}
                      >
                        &#8942;&#8942;
                      </span>
                    </td>
                    <td className={styles.td} style={{ textAlign: "center", color: "#999", fontSize: "11px" }}>
                      {index + 1}
                    </td>
                    <td className={styles.td}>
                      <input
                        type="text"
                        className={styles.templateField}
                        value={column.displayName}
                        onChange={(event) => handleColumnDisplayNameChange(column.key, event.target.value)}
                        disabled={isConfigurationLocked}
                        style={{ width: "160px", padding: "6px 8px", minHeight: "32px" }}
                      />
                      <div className={styles.inlineMetaText}>{column.apiField}</div>
                    </td>
                    <td className={styles.td}>
                      <input
                        type="text"
                        className={styles.templateField}
                        value={column.excelFormat}
                        onChange={(event) => handleColumnExcelFormatChange(column.key, event.target.value)}
                        disabled={isConfigurationLocked}
                        style={{ width: "84px", padding: "4px 6px", minHeight: "28px" }}
                      />
                      <div className={styles.inlineMetaText}>{mapColumnDataTypeToFriendlyLabel(column.dataType)}</div>
                    </td>
                    <td className={styles.td}>
                      <div className={styles.actionCell}>
                        <ActionIconButton
                          icon={<Dismiss12Regular />}
                          onClick={() => handleRemoveColumn(column.key)}
                          disabled={isConfigurationLocked}
                          title="Remove"
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className={styles.emptyRow}>
            {loadingColumns
              ? "Loading columns..."
              : "No columns selected. Select at least one column before generating."}
          </div>
        )}
      </div>
    </div>
  );
};

export default TemplateColumnsSection;
